import { useState, useCallback } from 'react';

export interface ScheduledProgram {
  streamId: string;
  categoryId?: string;
  channelName: string;
  channelIcon?: string;
  title: string;
  description?: string;
  startMs: number;
  endMs: number;
  addedAt: number;
}

const STORAGE_KEY = 'ott_player_schedules';

function loadFromStorage(): ScheduledProgram[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ScheduledProgram[]) : [];
  } catch {
    return [];
  }
}

function saveToStorage(items: ScheduledProgram[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // storage full or unavailable
  }
}

export function useSchedules() {
  const [schedules, setSchedules] = useState<ScheduledProgram[]>(loadFromStorage);

  const addSchedule = useCallback((program: ScheduledProgram) => {
    setSchedules((prev) => {
      const exists = prev.some(
        (s) => s.streamId === program.streamId && s.startMs === program.startMs,
      );
      if (exists) return prev;
      const next = [...prev, program];
      saveToStorage(next);
      return next;
    });
  }, []);

  const removeSchedule = useCallback((streamId: string, startMs: number) => {
    setSchedules((prev) => {
      const next = prev.filter(
        (s) => !(s.streamId === streamId && s.startMs === startMs),
      );
      saveToStorage(next);
      return next;
    });
  }, []);

  const toggleSchedule = useCallback(
    (program: ScheduledProgram) => {
      setSchedules((prev) => {
        const exists = prev.some(
          (s) => s.streamId === program.streamId && s.startMs === program.startMs,
        );
        const next = exists
          ? prev.filter(
              (s) => !(s.streamId === program.streamId && s.startMs === program.startMs),
            )
          : [...prev, program];
        saveToStorage(next);
        return next;
      });
    },
    [],
  );

  const isScheduled = useCallback(
    (streamId: string, startMs: number) =>
      schedules.some((s) => s.streamId === streamId && s.startMs === startMs),
    [schedules],
  );

  /** Stream IDs que possuem pelo menos um agendamento */
  const scheduledStreamIds = useCallback(
    () => new Set(schedules.map((s) => s.streamId)),
    [schedules],
  );

  return {
    schedules,
    addSchedule,
    removeSchedule,
    toggleSchedule,
    isScheduled,
    scheduledStreamIds,
  };
}
